import { Asset } from '@/api/generatedApi';
import { BulkRemoveFromGroupModal } from '@/components/modals/BulkRemoveFromGroupModal';
import { MoveToGroupModal } from '@/components/modals/MoveToGroupModal';
import { pluralize } from '@/utils/format';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { IconButton, Tooltip, useTheme } from '@mui/material';
import { useState } from 'react';
import { useBulkSelectionContext } from './BulkSelectionContext';

export const ReportBulkActions = ({
  reportId,
  groupId,
  assetCount,
}: {
  reportId: string;
  groupId: string;
  assetCount: number;
}) => {
  const bulkSelection = useBulkSelectionContext<Asset>();
  const theme = useTheme();
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);

  const selectedAssets = bulkSelection?.selectedRows ?? [];
  const isLoadingAll = bulkSelection?.isLoadingAll ?? false;
  const clearSelection = bulkSelection?.clearSelection ?? (() => {});
  const selectedCount = selectedAssets.length;

  const handleRemoveModalOpen = () => {
    setRemoveModalOpen(true);
  };

  const handleRemoveModalClose = () => {
    setRemoveModalOpen(false);
  };

  const handleRemoveComplete = () => {
    clearSelection();
  };

  const handleMoveModalOpen = () => {
    setMoveModalOpen(true);
  };

  const handleMoveModalClose = () => {
    setMoveModalOpen(false);
  };

  const handleMoveSuccess = () => {
    clearSelection();
  };

  return (
    <>
      <Tooltip
        title={isLoadingAll ? 'Data is loading' : `Move ${pluralize(selectedCount, 'asset', 'assets')} to group`}
      >
        <span>
          <IconButton
            size='small'
            onClick={handleMoveModalOpen}
            disabled={isLoadingAll}
            sx={{
              color: theme.palette.text.primary,
              '&:hover': { backgroundColor: theme.palette.action.hover },
            }}
          >
            <DriveFileMoveIcon fontSize='small' />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip
        title={isLoadingAll ? 'Data is loading' : `Remove ${pluralize(selectedCount, 'asset', 'assets')} from group`}
      >
        <span>
          <IconButton
            size='small'
            onClick={handleRemoveModalOpen}
            disabled={isLoadingAll}
            sx={{
              color: theme.palette.text.primary,
              '&:hover': { backgroundColor: theme.palette.action.hover },
            }}
          >
            <RemoveCircleOutlineIcon fontSize='small' />
          </IconButton>
        </span>
      </Tooltip>
      <MoveToGroupModal
        open={moveModalOpen}
        onClose={handleMoveModalClose}
        onSuccess={handleMoveSuccess}
        currentGroupId={groupId}
        assetIds={selectedAssets.map(asset => asset.id)}
        reportId={reportId}
      />
      <BulkRemoveFromGroupModal
        open={removeModalOpen}
        onClose={handleRemoveModalClose}
        selectedAssets={selectedAssets}
        reportId={reportId}
        groupId={groupId}
        assetCount={assetCount}
        onRemoveComplete={handleRemoveComplete}
      />
    </>
  );
};
